import {Component, OnInit} from '@angular/core';
import {ItemDataService} from '../../services/item-data.service';
import {BaseItem, BaseItemRequestPayload} from '@emm/common-items/models/item.model';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {
    CreateCommonItemDialogComponent
} from '@emm/common-items/dialogs/create-common-item-dialog/create-common-item-dialog.component';
import {environment} from '@env';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {NotificationService} from '@emm/shared/services/notification.service';
import {BehaviorSubject, combineLatest, forkJoin} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, shareReplay} from 'rxjs/operators';
import {Modifier} from '@emm/common-items/models/modifier.model';

@Component({
    selector: 'app-items',
    templateUrl: './items.component.html',
    styleUrls: ['./items.component.scss']
})
export class CommonItemsListComponent implements OnInit {

    protected readonly environment = environment;
    searchTerm = '';
    filter: 'ALL' | 'ITEM' | 'MODIFIER' | 'ITEM_GROUPING' | 'MODIFIER_GROUPING' | 'MIGRATED' = 'MIGRATED';

    items$ = new BehaviorSubject<BaseItem[]>([]);
    private searchTerm$ = new BehaviorSubject<string>('');
    private filter$ = new BehaviorSubject<string>(this.filter);
    private sortColumn$ = new BehaviorSubject<string | null>(null);
    private sortDirection$ = new BehaviorSubject<'asc' | 'desc'>('asc');

    filteredItems$ = combineLatest([
        this.items$,
        this.searchTerm$.pipe(distinctUntilChanged(), debounceTime(150)),
        this.filter$.pipe(distinctUntilChanged()),
        this.sortColumn$,
        this.sortDirection$
    ]).pipe(
        map(([items, search, filter, sortColumn, sortDirection]) => {
            let filtered = items;
            if (filter !== 'ALL') {
                filtered = filter === 'MIGRATED'
                    ? filtered.filter(item => item.migrated === true)
                    : filtered.filter(item => item.migrated === true && (item.class === filter || item.objectType === filter));
            }
            const searchNormalized = search.trim().toLowerCase();
            if (searchNormalized) {
                filtered = filtered.filter(item => {
                    const name = item?.name?.toLowerCase() || '';
                    const tag = item?.tag?.toLowerCase() || '';
                    return name.includes(searchNormalized) || tag.includes(searchNormalized);
                });
            }

            if (sortColumn) {
                filtered = [...filtered].sort((a, b) => {
                    let comparison = 0;
                    let aValue = '';
                    let bValue = '';

                    switch (sortColumn) {
                        case 'tag':
                            aValue = (a.tag || '').trim();
                            bValue = (b.tag || '').trim();
                            break;
                        case 'name':
                            aValue = (a.name || '').trim();
                            bValue = (b.name || '').trim();
                            break;
                        case 'class':
                            aValue = (a.class || a.objectType || '').trim();
                            bValue = (b.class || b.objectType || '').trim();
                            break;
                        case 'type':
                            aValue = (a.type || '').trim();
                            bValue = (b.type || '').trim();
                            break;
                    }

                    // Apply the same sorting logic as sortItems
                    const aFirstChar = aValue.charAt(0).toUpperCase();
                    const bFirstChar = bValue.charAt(0).toUpperCase();

                    let aCategory: number;
                    let bCategory: number;

                    if (/^[A-Z]$/.test(aFirstChar)) {
                        aCategory = 0;
                    } else if (/^[0-9]$/.test(aFirstChar)) {
                        aCategory = 1;
                    } else {
                        aCategory = 2;
                    }

                    if (/^[A-Z]$/.test(bFirstChar)) {
                        bCategory = 0;
                    } else if (/^[0-9]$/.test(bFirstChar)) {
                        bCategory = 1;
                    } else {
                        bCategory = 2;
                    }

                    if (aCategory !== bCategory) {
                        comparison = aCategory - bCategory;
                    } else if (aCategory === 1) {
                        const aMatch = aValue.match(/^(\d+)/);
                        const bMatch = bValue.match(/^(\d+)/);

                        if (aMatch && bMatch) {
                            const aNum = parseInt(aMatch[1], 10);
                            const bNum = parseInt(bMatch[1], 10);

                            if (aNum !== bNum) {
                                comparison = aNum - bNum;
                            } else {
                                comparison = aValue.toUpperCase().localeCompare(bValue.toUpperCase(), undefined, {sensitivity: 'base'});
                            }
                        } else {
                            comparison = aValue.toUpperCase().localeCompare(bValue.toUpperCase(), undefined, {sensitivity: 'base'});
                        }
                    } else {
                        comparison = aValue.toUpperCase().localeCompare(bValue.toUpperCase(), undefined, {sensitivity: 'base'});
                    }

                    return sortDirection === 'asc' ? comparison : -comparison;
                });
            }

            return filtered;
        }),
        shareReplay(1)
    );

    sortColumn: string | null = null;
    sortDirection: 'asc' | 'desc' = 'asc';

    hideButtonsToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.HIDE_BUTTONS);
    showCommonItems$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_COMMON_ITEMS);
    isSuperAdmin = false;
    isLoading = true;

    constructor(
        private dialog: MatDialog,
        private itemDataService: ItemDataService,
        private router: Router,
        private launchDarklyService: LaunchDarklyService,
        private userService: UserService,
        private notificationService: NotificationService,
        ) {}

    ngOnInit() {
        this.isSuperAdmin = this.userService.hasSuperAdminRole();
        this.loadAllCommonItems();
    }

    sortItems(a: BaseItem, b: BaseItem): number {
        const info = (item: BaseItem) => {
            const primary = (item.name && item.name.trim()) ? item.name.trim() : (item.tag || '').trim();
            const firstChar = primary.charAt(0).toUpperCase();
            let category: number;
            if (/^[A-Z]$/.test(firstChar)) {
                category = 0;
            } else if (/^[0-9]$/.test(firstChar)) {
                category = 1;
            } else {
                category = 2;
            }
            let numericValue: number | null = null;
            if (category === 1) {
                const match = primary.match(/^(\d+)/);
                if (match) {
                    numericValue = parseInt(match[1], 10);
                }
            }
            return {category, primary: primary.toUpperCase(), numericValue};
        };
        const aInfo = info(a);
        const bInfo = info(b);
        if (aInfo.category !== bInfo.category) {
            return aInfo.category - bInfo.category;
        }

        if (aInfo.category === 1) {
            if (aInfo.numericValue !== null && bInfo.numericValue !== null && aInfo.numericValue !== bInfo.numericValue) {
                return aInfo.numericValue - bInfo.numericValue;
            }
        }
        return aInfo.primary.localeCompare(bInfo.primary, undefined, {sensitivity: 'base'});
    }

    loadAllCommonItems() {
        forkJoin({
            itemsResponse: this.itemDataService.getItems(),
            modifierGroupsResponse: this.itemDataService.getModifierGroups(),
            modifiersResponse: this.itemDataService.getModifiers()
        }).subscribe({
            next: ({itemsResponse, modifierGroupsResponse, modifiersResponse}) => {
                this.items$.next(
                    [
                        ...itemsResponse,
                        ...modifierGroupsResponse,
                        ...modifiersResponse
                    ].sort((a, b) => this.sortItems(a, b))
                );
                this.isLoading = false;
            },
            error: () => {
                this.notificationService.error(`Failed to load common items`);
                this.isLoading = false;
            }
        });
    }

    reloadItemsByClass(itemClass: string) {
        const currentItems = this.items$.value;
        if (itemClass === 'ITEM' || itemClass === 'ITEM_GROUPING') {
            this.itemDataService.getItems().subscribe({
                next: (itemsResponse) => {
                    const otherItems = currentItems.filter(item =>
                        item.class !== 'ITEM' && item.class !== 'ITEM_GROUPING' &&
                        item.objectType !== 'ITEM' && item.objectType !== 'ITEM_GROUPING'
                    );
                    this.items$.next([...itemsResponse, ...otherItems].sort((a, b) => this.sortItems(a, b)));
                },
                error: () => {
                    this.notificationService.error(`Failed to reload items`);
                }
            });
        } else if (itemClass === 'MODIFIER') {
            this.itemDataService.getModifiers().subscribe({
                next: (modifiersResponse) => {
                    const otherItems = currentItems.filter(item =>
                        item.class !== 'MODIFIER' && item.objectType !== 'MODIFIER'
                    );
                    this.items$.next([...modifiersResponse, ...otherItems].sort((a, b) => this.sortItems(a, b)));
                },
                error: () => {
                    this.notificationService.error(`Failed to reload modifiers`);
                }
            });
        } else if (itemClass === 'MODIFIER_GROUPING') {
            this.itemDataService.getModifierGroups().subscribe({
                next: (modifierGroupsResponse) => {
                    const otherItems = currentItems.filter(item =>
                        item.class !== 'MODIFIER_GROUPING' && item.objectType !== 'MODIFIER_GROUPING'
                    );
                    this.items$.next([...modifierGroupsResponse, ...otherItems].sort((a, b) => this.sortItems(a, b)));
                },
                error: () => {
                    this.notificationService.error(`Failed to reload modifier groups`);
                }
            });
        }
    }

    setFilter(filter: typeof this.filter) {
        this.filter = filter;
        this.filter$.next(filter);
    }

    onSearchTermChange(term: string) {
        this.searchTerm = term;
        this.searchTerm$.next(term);
    }

    sortByColumn(column: string) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.sortColumn$.next(this.sortColumn);
        this.sortDirection$.next(this.sortDirection);
    }

    resetSorting() {
        this.sortColumn = null;
        this.sortDirection = 'asc';
        this.sortColumn$.next(null);
        this.sortDirection$.next('asc');
    }

    trackByTag(index: number, item: BaseItem) {
        return item.tag;
    }

    goToDetail(item: BaseItem) {
        if (item.class === 'ITEM' || item.objectType === 'ITEM') {
            this.router.navigate(['/common-items/item-detail', item.tag]);
        } else if (item.class === 'ITEM_GROUPING'|| item.objectType === 'ITEM_GROUPING') {
            this.router.navigate(['/common-items/item-group-detail', item.tag]);
        } else if (item.class === 'MODIFIER_GROUPING' || item.objectType === 'MODIFIER_GROUPING') {
            this.router.navigate(['/common-items/modifier-group-detail', item.tag]);
        } else if (item.class === 'MODIFIER'|| item.objectType === 'MODIFIER') {
            this.router.navigate(['/common-items/modifier-detail', item.tag]);
        }
    }

    openCreateCommonItemDialog() {
        this.dialog.open(CreateCommonItemDialogComponent, {
            width: '750px',
            height: '750px',
        }).afterClosed().pipe().subscribe((result: {
            newItem: BaseItemRequestPayload | Modifier, requestedBy: string, class: string
        }) => {
            if (!result) {
                return;
            }

            console.log(result);

            if (result.class === 'MODIFIER') {
                const payload = result.newItem as BaseItemRequestPayload;
                const modifier: Modifier = {
                    tag: payload.tag,
                    name: payload.name,
                    image: payload.image,
                    description: payload.description,
                    modifierType: payload.attributes?.find(attr => attr.jsonProperty === 'modifierType')?.value,
                    type: payload.type,
                    rmi: payload.rmi,
                    disabled: false,
                    locationSetTag: payload.locationSetTag,
                    country: payload.country,
                    isLocked: payload.isLocked,
                    hideNutrition: payload.attributes?.find(attr => attr.jsonProperty === 'hideNutrition')?.value,
                    maximum: payload.attributes?.find(attr => attr.jsonProperty === 'maximum')?.value,
                };
                this.itemDataService.addModifier(modifier, result.requestedBy).subscribe({
                    next: (response) => {
                        if (response) {
                            this.notificationService.success(`Successfully created ${result.newItem.tag}`);
                            this.reloadItemsByClass(result.class);
                        }
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    },
                });
            } else if (result.class === 'MODIFIER_GROUPING') {
                const payload = result.newItem as BaseItemRequestPayload;
                const modifierGroup = {
                    tag: payload.tag,
                    name: payload.name,
                    image: payload.image,
                    description: payload.description,
                    modifierTypeGrouping: payload.attributes?.find(attr => attr.jsonProperty === 'modifierTypeGrouping')?.value,
                    type: payload.type,
                    locationSetTag: payload.locationSetTag,
                    country: payload.country,
                    maximum: payload.attributes?.find(attr => attr.jsonProperty === 'maximum')?.value,
                    components: payload.components || [],
                    isLocked: payload.isLocked,
                    disabled: false
                };
                this.itemDataService.addModifierGroup(modifierGroup, result.requestedBy).subscribe({
                    next: (response) => {
                        if (response) {
                            this.notificationService.success(`Successfully created ${result.newItem.tag}`);
                            this.reloadItemsByClass(result.class);
                        }
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    },
                });
            } else {
                this.itemDataService.createItem(result.newItem as BaseItemRequestPayload, result.requestedBy).subscribe({
                    next: (response) => {
                        if (response) {
                            this.notificationService.success(`Successfully created ${result.newItem.tag}`);
                            this.reloadItemsByClass(result.class);
                        }
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    },
                });
            }
        });
    }
}
